/** @jsx CanvaSX */
import { CanvaSX } from '@lib/canvasx/canvasx';
import ObjectValue, { type FieldObjectType } from './ObjectValue';
import { Text } from '@lib/canvasx/components/Text';
import { FlexRow } from '@lib/canvasx/components/FlexRow';
import { Rect } from '@lib/canvasx/components/Rect';
import { Emoji } from '../canvasx/Emoji';
import { reactionKey } from './ReactionsBehaviors';
import { isShape } from './guards';
import {
  type ButtonState,
  ButtonStyles,
  formattedRgba,
} from '../canvasx/buttonStyles';

class ReactionAction extends ObjectValue {
  static shouldRender(object: FieldObjectType): boolean {
    return Boolean(
      isShape(object) && object.reactions && object.shapeName === 'note'
    );
  }

  static renderFields(
    maxChildWidth: number,
    object: FieldObjectType
  ): CanvaSXNode[] {
    if (!object.reactions || !isShape(object) || object.shapeName !== 'note') {
      return [];
    }

    const reactions = object.reactionVotes;
    if (!reactions || Object.keys(reactions).length === 0) {
      return [];
    }

    let reactionCount = 0;

    return Object.entries(reactions)
      .map(([shortCode, votes]) => {
        // Don't render more than 6 reactions at once.
        if (votes === 0 || reactionCount >= 6) {
          return null;
        }
        reactionCount++;

        const key = reactionKey(shortCode);
        const hovering = object.hoveredKey === key;
        const selected = object.isSelected();

        let buttonState: ButtonState = 'default';
        if (hovering) {
          buttonState = 'hovered';
        } else if (selected) {
          buttonState = 'selected';
        }

        const { background, icon } = ButtonStyles[buttonState];

        return (
          <Rect
            fill={formattedRgba(background)}
            borderRadius={2}
            padding={2}
            key={key}
            onClick={() => {
              const { userId } = object?.controller?.state;
              if (userId) {
                object.toggleReaction({
                  shortCode,
                  userId,
                });

                object.updateTooltipAfterRender(shortCode);
              }
            }}
            tooltip={object.reactionsTooltipContent}
          >
            <FlexRow gap={2} verticalAlignment='center'>
              <Emoji shortCode={shortCode} />
              <Text color={formattedRgba(icon)}>{votes}</Text>
            </FlexRow>
          </Rect>
        );
      })
      .filter(Boolean);
  }
}

export default ReactionAction;
